import React from 'react'
import PropTypes from "prop-types"
import moment from "moment";
import Component, {onChange} from "./Component";
import {dateToString, dateToStringInZone, prettyDateFormat, stringToDate} from "../../utils/DateUtils";

// Deliberately browser-local (unlike dateToString's pretty branch) - formats the calendar's own
// navigation Date, built entirely from native Date getters/setters.
function formatCalendarDate(date, dateOnly) {
  return moment(date).format(prettyDateFormat(!dateOnly));
}

class DateTimePicker extends Component {
  constructor(props) {
    super(props);
    this.input = React.createRef();
    this.container = React.createRef();

    this.translations = this.props.translations ? {...DEFAULT_TRANSLATIONS, ...this.props.translations} : DEFAULT_TRANSLATIONS;

    this.currentActive = undefined;

    const rawValue = super.getValue();
    let value;

    if (typeof rawValue === 'string') {
      value = stringToDate(rawValue);
    } else if (typeof rawValue === 'number') {
      value = new Date(rawValue);
    } else if (rawValue instanceof Date) {
      value = rawValue;
    } else {
      value = undefined;
    }

    const date = value || this.props.defaultDate || new Date();
    const year = new StateYear(date);
    const month = new StateMonth(date, this.translations);
    const day = new StateDay(date, this.translations);
    const hour = new StateHour(date, this.translations);
    const minute = new StateMinute(date, this.translations);

    year.setState(undefined, month);
    month.setState(year, day);
    if (this.props.type === "datetime") {
      day.setState(month, hour);
      hour.setState(day, minute);
      minute.setState(hour, undefined);
    } else {
      day.setState(month, undefined);
    }

    this.selector = day;

    this.state = {
      data: this.selector.getState(),
      initial: !!value
    };

    this._onDropdownClick = this._onDropdownClick.bind(this);
    this._onInputKeyDown = this._onInputKeyDown.bind(this); //we bound keydown method, since we need the same method to remove event
  }

  componentDidMount() {
    //we use plain events for keydown since we want to cancel them, which is not possible with react synthetic events
    this.input.current.addEventListener("keydown", this._onInputKeyDown, false);
    //we use plain mousedown events so we can cancel them, this way we can keep dropdown visible on click
    this.container.current.addEventListener("mousedown", this._onDropdownClick, false);
  }

  componentWillUnmount() {
    this.input.current.removeEventListener("keydown", this._onInputKeyDown, false);
    this.container.current.removeEventListener("mousedown", this._onDropdownClick, false);
  }
  getNormalizedDate(value = this.props.value) {
    if (!value) return null;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') return stringToDate(value);
    if (value instanceof Date) return value;
    return null;
  }
  componentDidUpdate(prevProps) {
    const vdu = this.valueDidUpdate(prevProps);
    if (vdu) {
      const date = this.getNormalizedDate();

      if (!date) return;

      if (this.props.useEpoch) {
        // An epoch value is a raw zone-agnostic instant - the zone-label/relabel round-trip below
        // is only meaningful for the ISO/pretty display modes. Applying it here would reparse the
        // configured-zone digits as browser-local time, silently shifting the epoch by the zone
        // delta whenever the two zones differ. Sync directly to the real instant instead.
        const needsUpdate = this.state.data.date.getTime() !== date.getTime();
        if (needsUpdate) {
          this.selector.updateDate(date);
          this.input.current.value = String(date.getTime());
          this.setState({
            ...this.state,
            data: this.selector.getState(),
            initial: true
          }, () => this.onChange(this._getDateValue()));
        }
        return;
      }

      const inputValue = this.input.current.value;

      const pickedDigitsNow = dateToStringInZone(date, this.props.type !== "datetime");
      const needsUpdate = inputValue !== pickedDigitsNow;

      // Reconstruct a Date whose *local* reading equals the configured-zone digits, since the
      // calendar's navigation classes are built entirely on native (browser-local) Date getters/setters.
      const localDate = stringToDate(pickedDigitsNow, true, this.props.type === "datetime");
      if (!localDate) return;

      this.selector.updateDate(localDate);
      this.input.current.value = pickedDigitsNow;

      this.setState({
        ...this.state,
        data: this.selector.getState(),
        initial: !!date
      }, () => {
        if (needsUpdate) {
          this.onChange(this._getDateValue());
        }
      });
    }
  }

  render() {
    return (
      <label className={"field has-float-label dropdown date-time loadable " + (this.props.className ? this.props.className : "")}>
        <input ref={this.input}
               id={this.props.id}
               name={this.props.id}
               defaultValue={this._getDateValue()}
               disabled={this.props.disabled}
               type="text"
               placeholder=" "
               className={this.isInvalid() ? "is-invalid input" : "input"}
               onInput={this._onInputChange.bind(this)}
               onBlur={this._onInputBlur.bind(this)}/>
        <span>{this.props.label}</span>
        <div className="error-icon fz fz-exclamation-triangle solid">
          <div className="has-tooltip danger" data-tooltip={this.errorMessage()}/>
        </div>
        <div className="dropdown-indicator right-decoration fz fz-calendar-alt solid"/>
        <div ref={this.container}
             className="dropdown-container"
             data-state={this.state.data.id}
             onMouseMove={this._onDropdownMouseMove.bind(this)}>
          <div className="header">
            <div data-item-type="page-left" className="item left fz fz-chevron-left disable-select">&nbsp;</div>
            <div data-item-type="up" className="item current disable-select">{this.state.data.header}</div>
            <div data-item-type="page-right" className="item right fz fz-chevron-right disable-select">&nbsp;</div>
          </div>
          <hr/>
          <div className="picker">
            {
              this.state.data.items.map((item, index) =>
                <div key={index}
                     data-item-type={item.type || "dynamic"}
                     data-item-value={item.value !== undefined ? item.value : item.label}
                     className={this._itemClass(item)}>{item.label}</div>
              )
            }
          </div>
        </div>
      </label>
    )
  }

  getValue() {
    let value = super.getValue();

    if (typeof value === 'string') {
      value = stringToDate(value); // Convert string to Date object
    }

    if (this.props.useEpoch && value instanceof Date) {
      return value.getTime(); // Convert Date to epoch if needed
    }

    // Component's base onChange(event) ignores its argument and calls getEventValue()/getValue()
    // instead, so this is the real value consumers receive - the wire format (ISO, app-configured-
    // zone offset), not the display format (that stays pretty/browser-local, see _getDateValue()).
    return dateToString(value, this.props.type !== 'datetime', false); // Convert Date back to string
  }
  getEventValue() {
    const date = this.state.data.date;

    if (this.props.useEpoch && date instanceof Date) {
      return date.getTime(); // Convert Date to epoch if needed
    }

    // ISO/offset branch - see getValue() above.
    return dateToString(date, this.props.type !== 'datetime', false); // Convert Date back to string
  }

  _onInputChange(event) {
    const date = stringToDate(event.currentTarget.value, true, this.props.type === "datetime");
    if (date) {
      if (this.props.type === "datetime") {
        //round (floor) minutes to 5 minute
        date.setMinutes(Math.floor(date.getMinutes() / 5) * 5);
        this.input.current.value = formatCalendarDate(date, false);
      }

      this.selector.updateDate(date);
      this.setState({
        ...this.state,
        data: this.selector.getState()
      });

      let value = date;
      if (this.props.useEpoch) {
        value = date.getTime(); // Convert Date to epoch
      }
      this.onChange(value);
    } else if (event.currentTarget.value.length === 0) {
      this.setState({
        ...this.state,
        data: {...this.selector.getState(), date: null}
      });
      onChange(this.props, null);
    }
  }

  _onInputBlur(event) {
    if (!stringToDate(event.currentTarget.value, true, this.props.type === "datetime") && event.currentTarget.value.length > 0) {
      const date = stringToDate(event.currentTarget.value, true); //get date without time
      if (date && this.props.type === "datetime") {
        //if we only type date in the input, keep old time and set new date
        date.setHours(this.selector.getDate().getHours());
        date.setMinutes(this.selector.getDate().getMinutes());
        this.selector.updateDate(date);
        this.setState({
          ...this.state,
          data: this.selector.getState()
        });
        this.onChange(this._getDateValue());
      }
      //if input string is invalid date and not empty string set last valid date
      this.input.current.value = formatCalendarDate(this.selector.date, this.props.type !== "datetime")
    }
    setTimeout(() => this.onBlur(event), 100); //ugly hack that prevents on blur validation before state is set
  }

  _onInputKeyDown(event) {
    switch (event.key) {
      case "Escape":
        this.input.current.blur();
        event.preventDefault();
        return false;
      case "Enter":
        if (this.getValue() === null) {
          const state = this.selector.getState();
          this.input.current.value = formatCalendarDate(state.date, this.props.type !== "datetime");

          this.setState({
            ...this.state,
            data: this.selector.getState()
          }, () => this.onChange(this._getDateValue()));
        }
        this.input.current.blur();
        event.preventDefault();
        return false;
    }
  }

  _onDropdownClick(event) {
    if (this._isItem(event.target)) {
      const hideDropdown = this._isItemDynamic(event.target) && !this.selector.nextState;
      this._selectItem(event.target);

      if (!hideDropdown) {
        event.preventDefault(); //prevent closing dropdown
        return false;
      }
    }
  }

  _onDropdownMouseMove(event) {
    if (this._isItem(event.target)) {
      this._hoverItem(event.target);
    }
  }

  _selectItem(item) {
    switch (item.getAttribute("data-item-type")) {
      case "dynamic":
      case "dynamic-special":
        // Whether the level just clicked (day for type="date"; minute for type="datetime") has no
        // further level to advance to - i.e. this click is the terminal one for a full pick, not
        // an intermediate step (e.g. day, with hour/minute still to come). Optionally reported via
        // onComplete below - every DateTimePicker usage that doesn't pass onComplete is unaffected.
        const isFinalLevel = !this.selector.nextState;
        const initial = this.state.initial || isFinalLevel;
        this.selector.selectItem(item.getAttribute("data-item-value"));
        this.selector = this.selector.getNextState();
        const data = this.selector.getState();
        this.input.current.value = formatCalendarDate(data.date, this.props.type !== "datetime");
        this.setState({
          ...this.state,
          data: data,
          initial: initial
        }, () => {
          this.onChange(this._getDateValue()); //since change event is not triggered, we call change manually with date value
          if (isFinalLevel && this.props.onComplete) {
            this.props.onComplete(this._getDateValue());
          }
        });
        this._unHoverActiveItem();
        break;
      case "page-left":
        this.selector.pageLeft();
        this.setState({
          ...this.state,
          data: this.selector.getState()
        });
        break;
      case "page-right":
        this.selector.pageRight();
        this.setState({
          ...this.state,
          data: this.selector.getState()
        });
        break;
      case "up":
        this.selector = this.selector.getPreviousState();
        this.setState({
          ...this.state,
          data: this.selector.getState()
        });
        break;
    }
  }
  _getDateValue() {
    if (!this.state.initial) {
      return "";
    }

    if (this.props.useEpoch) {
      return this.state.data.date.getTime(); // Return epoch number
    }

    return formatCalendarDate(this.state.data.date, this.props.type !== "datetime");
  }

  _itemClass(item) {
    const column = GRID_COLUMN_CLASS_MAP[this.state.data.columnCount] || "";
    const active = item.active ? " active" : "";
    const type = item.type ? " " + item.type : "";
    const additionalClass = item.class ? " " + item.class : "";
    return "item ".concat(column, active, type, additionalClass);
  }

  _hoverItem(item) {
    if (this.currentActive) {
      this.currentActive.classList.remove("hover");
    }
    this.currentActive = item;
    this.currentActive.classList.add("hover");
  }

  _unHoverActiveItem() {
    if (this.currentActive) {
      this.currentActive.classList.remove("hover");
      this.currentActive = undefined;
    }
  }

  _isItem(item) {
    return item && item.hasAttribute("data-item-type") && item.getAttribute("data-item-type") !== "head";
  }

  _isItemDynamic(item) {
    return item && ["dynamic", "dynamic-special"].includes(item.getAttribute("data-item-type"));
  }
}


const GRID_COLUMN_CLASS_MAP = {
  4: "is-one-quarter",
  7: "is-one-seventh"
};

const DEFAULT_TRANSLATIONS = {
  month: {
    1: "Januar",
    2: "Februar",
    3: "Marec",
    4: "April",
    5: "Maj",
    6: "Junij",
    7: "Julij",
    8: "Avgust",
    9: "September",
    10: "Oktober",
    11: "November",
    12: "December"
  },
  monthShort: {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "Maj",
    6: "Jun",
    7: "Jul",
    8: "Avg",
    9: "Sep",
    10: "Okt",
    11: "Nov",
    12: "Dec"
  },
  dayOfWeekShort: {
    1: "Pon",
    2: "Tor",
    3: "Sre",
    4: "Čet",
    5: "Pet",
    6: "Sob",
    7: "Ned"
  }
};

class State {
  constructor(id, columnCount, date, translations) {
    this.id = id;
    this.columnCount = columnCount;
    this.translations = translations;
    this.date = date;
    this.nextState = undefined;
    this.previousState = undefined;
  }

  updateDate(date) {
    this.date.setTime(date.getTime())
  }

  getDate() {
    return this.date;
  }

  getItems() {
    throw new Error("Method getItems is not implemented!");
  }

  getHeader() {
    throw new Error("Method getHeader is not implemented!");
  }

  getNextState() {
    return this.nextState || this;
  }

  getPreviousState() {
    return this.previousState || this;
  }

  setState(previous, next) {
    this.previousState = previous;
    this.nextState = next;
  }

  selectItem() {
    throw new Error("Method selectItem is not implemented!");
  }

  pageLeft() {
    throw new Error("Method pageLeft is not implemented!");
  }

  pageRight() {
    throw new Error("Method pageRight is not implemented!");
  }

  getState() {
    return {
      id: this.id,
      date: this.date,
      items: this.getItems(),
      columnCount: this.columnCount,
      header: this.getHeader()
    };
  }
}

class StateYear extends State {
  constructor(date) {
    super("year", 4, date);
  }

  getItems() {
    let year = this.date.getFullYear() - 5;
    const years = [
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++},
      {label: year++}];

    const activeYear = this.date.getFullYear();

    return years.map(function (item) {
      if (item.label === activeYear) {
        item.active = true;
      }
      return item;
    });
  }

  getHeader() {
    return (this.date.getFullYear() - 5) + " - " + (this.date.getFullYear() + 6);
  }

  selectItem(year) {
    year = parseInt(year);
    this.date.setFullYear(year);
  }

  pageLeft() {
    this.date.setFullYear(this.date.getFullYear() - 12);
  }

  pageRight() {
    this.date.setFullYear(this.date.getFullYear() + 12);
  }
}

class StateMonth extends State {
  constructor(date, translations) {
    super("month", 4, date, translations);
  }

  getItems() {
    const months = [
      {value: 1, label: this.translations.monthShort[1]},
      {value: 2, label: this.translations.monthShort[2]},
      {value: 3, label: this.translations.monthShort[3]},
      {value: 4, label: this.translations.monthShort[4]},
      {value: 5, label: this.translations.monthShort[5]},
      {value: 6, label: this.translations.monthShort[6]},
      {value: 7, label: this.translations.monthShort[7]},
      {value: 8, label: this.translations.monthShort[8]},
      {value: 9, label: this.translations.monthShort[9]},
      {value: 10, label: this.translations.monthShort[10]},
      {value: 11, label: this.translations.monthShort[11]},
      {value: 12, label: this.translations.monthShort[12]}];
    months[this.date.getMonth()].active = true;
    return months;
  }

  getHeader() {
    return this.date.getFullYear();
  }

  selectItem(month) {
    month = parseInt(month);
    this.date.setMonth(month - 1);
  }

  pageLeft() {
    this.date.setFullYear(this.date.getFullYear() - 1);
  }

  pageRight() {
    this.date.setFullYear(this.date.getFullYear() + 1);
  }
}

class StateDay extends State {
  constructor(date, translations) {
    super("day", 7, date, translations);
  }

  getItems() {
    let date = new Date(this.date.getFullYear(), this.date.getMonth(), 1);
    const offsetStart = (date.getDay() + 6) % 7; //day of week index (+6 % 7 since 0 is Sunday)
    date.setDate(date.getDate() - offsetStart);

    function incrementDay(date) {
      date.setDate(date.getDate() + 1);
      return date;
    }

    const days = [];
    //headers
    for (let i = 1, month = this.date.getMonth(); i < 8; i++) {
      days.push({label: this.translations.dayOfWeekShort[i], month: month, type: "head"})
    }

    //test if we need 5 (35 days) or 6 (42 days) lines
    let dayCount = 35;
    date.setDate(date.getDate() + 35);
    if (this.date.getMonth() === date.getMonth()) {
      // since after 35 days (7 lines) we are still at the same month, add additional line
      dayCount += 7;
    }
    date.setDate(date.getDate() - 35);

    //days
    date.setDate(date.getDate() - 1); //we go one day before, since we increment it each step
    const currentMonth = this.date.getMonth();
    const currentDay = this.date.getDate();
    for (let i = 0; i < dayCount; i++) {
      const day = incrementDay(date).getDate();
      const month = date.getMonth();
      const item = {label: day, month: month};

      if (month < currentMonth) {
        item.type = "dynamic-special";
        item.value = "-" + day;
      } else if (month > currentMonth) {
        item.type = "dynamic-special";
        item.value = "+" + day;
      } else if (day === currentDay) {
        item.active = true;
      }
      days.push(item);
    }

    return days;
  }

  getHeader() {
    return this.translations.month[this.date.getMonth() + 1] + " " + this.date.getFullYear();
  }

  selectItem(day) {
    if (day.startsWith("-")) {
      day = day.replace("-", "");
      this.pageLeft();
    } else if (day.startsWith("+")) {
      day = day.replace("+", "");
      this.pageRight();
    }
    day = parseInt(day);
    day && this.date.setDate(day);
  }

  pageLeft() {
    this.date.setMonth(this.date.getMonth() - 1);
  }

  pageRight() {
    this.date.setMonth(this.date.getMonth() + 1);
  }
}

class StateHour extends State {
  constructor(date, translations) {
    super("hour", 4, date, translations);
  }

  getItems() {
    const hours = [
      {label: "0:00", value: 0, class: "short"},
      {label: "1:00", value: 1, class: "short"},
      {label: "2:00", value: 2, class: "short"},
      {label: "3:00", value: 3, class: "short"},
      {label: "4:00", value: 4, class: "short"},
      {label: "5:00", value: 5, class: "short"},
      {label: "6:00", value: 6, class: "short"},
      {label: "7:00", value: 7, class: "short"},
      {label: "8:00", value: 8, class: "short"},
      {label: "9:00", value: 9, class: "short"},
      {label: "10:00", value: 10, class: "short"},
      {label: "11:00", value: 11, class: "short"},
      {label: "12:00", value: 12, class: "short"},
      {label: "13:00", value: 13, class: "short"},
      {label: "14:00", value: 14, class: "short"},
      {label: "15:00", value: 15, class: "short"},
      {label: "16:00", value: 16, class: "short"},
      {label: "17:00", value: 17, class: "short"},
      {label: "18:00", value: 18, class: "short"},
      {label: "19:00", value: 19, class: "short"},
      {label: "20:00", value: 20, class: "short"},
      {label: "21:00", value: 21, class: "short"},
      {label: "22:00", value: 22, class: "short"},
      {label: "23:00", value: 23, class: "short"}];
    hours[this.date.getHours()].active = true;
    return hours;
  }

  getHeader() {
    return this.date.getDate() + " " + this.translations.month[this.date.getMonth() + 1] + " " + this.date.getFullYear();
  }

  selectItem(hour) {
    this.date.setHours(parseInt(hour));
  }

  pageLeft() {
    this.date.setDate(this.date.getDate() - 1);
  }

  pageRight() {
    this.date.setDate(this.date.getDate() + 1);
  }
}

class StateMinute extends State {
  constructor(date, translations) {
    super("minute", 4, date, translations);
  }

  getItems() {
    // Rounding raw minutes 58/59 to the nearest 5-minute mark yields 60, not a valid slot in this
    // 12-item list (0-55) - roll that into the next hour's :00 instead, before reading the hour for
    // the labels below (otherwise they'd show the hour this rolled over FROM).
    const roundedMinute = Math.round(this.date.getMinutes() / 5) * 5;
    if (roundedMinute === 60) {
      this.date.setHours(this.date.getHours() + 1, 0);
    } else {
      this.date.setMinutes(roundedMinute);
    }

    const hour = this.date.getHours();
    const minutes = [
      {label: hour + ":00", value: 0},
      {label: hour + ":05", value: 5},
      {label: hour + ":10", value: 10},
      {label: hour + ":15", value: 15},
      {label: hour + ":20", value: 20},
      {label: hour + ":25", value: 25},
      {label: hour + ":30", value: 30},
      {label: hour + ":35", value: 35},
      {label: hour + ":40", value: 40},
      {label: hour + ":45", value: 45},
      {label: hour + ":50", value: 50},
      {label: hour + ":55", value: 55}];
    minutes[this.date.getMinutes() / 5].active = true;
    return minutes;
  }

  getHeader() {
    return String(this.date.getDate()).concat(" ", this.translations.month[this.date.getMonth() + 1], " ", this.date.getFullYear(), " ",
      this.date.getHours(), ":", String(this.date.getMinutes()).padStart(2, "0"));
  }

  selectItem(minutes) {
    this.date.setMinutes(parseInt(minutes));
  }

  pageLeft() {
    this.date.setHours(this.date.getHours() - 1);
  }

  pageRight() {
    this.date.setHours(this.date.getHours() + 1);
  }
}

DateTimePicker.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf(["date", "datetime"]),
  // Accept: ISO-ish string, epoch number, or Date instance
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date)
  ]),
  defaultDate: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  label: PropTypes.string,
  isInvalid: PropTypes.bool,
  invalidMessage: PropTypes.string,
  translations: PropTypes.object,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  formField: PropTypes.object,
  useEpoch: PropTypes.bool,
  // Optional - fires once per pick, only on the terminal level (day for "date", minute for
  // "datetime"), unlike onChange which fires once per level. Every other usage never sets this.
  onComplete: PropTypes.func,
};

DateTimePicker.defaultProps = {
  // ... other default props
  type: "date",
  useEpoch: false, // Default to false for backward compatibility
};
export default DateTimePicker
